/**
 * `templatesApi` — standalone RTK Query slice for the Templates tab in
 * the Dashboards/Templates explorer. Backed by the proxy-server
 * `template_folders` / `templates` Postgres tables (see
 * `proxy-server/internal/ontology/migrate/054_templates_init.sql`) and
 * routed through the `axilon-api-service` BFF under `/api/ontology/*`
 * (`templates-routes.ts` → `ProxyTemplatesClient`), so the browser
 * never talks to the proxy directly.
 *
 * Mirrors the shape of `dashboardsApi` — same pattern, same
 * `ontologyBaseQuery` (auth + branch headers), independent reducer +
 * tag space (`'TemplateFolder'` / `'Template'`). The single
 * structural difference vs dashboards is the combined
 * `updateTemplateContent` mutation that writes `code` and
 * `default_values` together (the proxy persists them atomically;
 * the editor and the agent's `add_template`/`edit_template` tools
 * always save them as a pair).
 *
 * Both list calls return the full set: the explorer needs every row
 * to derive its tree and the row volume is expected to stay small.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { getOntologyServiceBaseUrl } from '../../config'
import { ontologyBaseQuery } from '../core'
import type {
  CreateTemplateFolderInput,
  CreateTemplateInput,
  MoveTemplateFolderInput,
  MoveTemplateFolderResponse,
  PatchTemplateInput,
  RenameTemplateInput,
  Template,
  TemplateFolder,
  UpdateTemplateContentInput
} from '@atbash-labs/proxy-client'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export const TEMPLATES_TAG_TYPES = ['TemplateFolder', 'Template'] as const
export type TemplatesTagType = (typeof TEMPLATES_TAG_TYPES)[number]

export const templatesApi = createApi({
  reducerPath: 'templatesApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: TEMPLATES_TAG_TYPES,
  endpoints: (builder) => ({
    // ── Template folders ────────────────────────────────────────────
    listTemplateFolders: builder.query<TemplateFolder[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/template-folders`,
      transformResponse: (response: { folders?: TemplateFolder[] }) => response.folders ?? [],
      providesTags: ['TemplateFolder']
    }),
    createTemplateFolder: builder.mutation<TemplateFolder, CreateTemplateFolderInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/template-folders`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TemplateFolder']
    }),
    renameTemplateFolder: builder.mutation<
      TemplateFolder,
      { id: string; input: RenameTemplateInput }
    >({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/template-folders/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: input
      }),
      invalidatesTags: ['TemplateFolder']
    }),
    deleteTemplateFolder: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ONTOLOGY_SERVICE_URL}/template-folders/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['TemplateFolder', 'Template']
    }),
    moveTemplateFolder: builder.mutation<MoveTemplateFolderResponse, MoveTemplateFolderInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/template-folders/move`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TemplateFolder', 'Template']
    }),
    deleteTemplateFolderByPath: builder.mutation<void, { path: string }>({
      query: ({ path }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/template-folders/delete`,
        method: 'POST',
        body: { path }
      }),
      invalidatesTags: ['TemplateFolder', 'Template']
    }),

    // ── Templates ───────────────────────────────────────────────────
    listTemplates: builder.query<Template[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/templates`,
      transformResponse: (response: { templates?: Template[] }) => response.templates ?? [],
      providesTags: ['Template']
    }),
    createTemplate: builder.mutation<Template, CreateTemplateInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/templates`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Template']
    }),
    renameTemplate: builder.mutation<Template, { id: string; input: PatchTemplateInput }>({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/templates/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: input
      }),
      invalidatesTags: ['Template', 'TemplateFolder']
    }),
    deleteTemplate: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ONTOLOGY_SERVICE_URL}/templates/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Template']
    }),
    updateTemplateContent: builder.mutation<
      Template,
      { id: string; input: UpdateTemplateContentInput }
    >({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/templates/${encodeURIComponent(id)}/content`,
        method: 'PUT',
        body: input
      }),
      invalidatesTags: ['Template']
    })
  })
})

export const {
  useListTemplateFoldersQuery,
  useCreateTemplateFolderMutation,
  useRenameTemplateFolderMutation,
  useDeleteTemplateFolderMutation,
  useMoveTemplateFolderMutation,
  useDeleteTemplateFolderByPathMutation,
  useListTemplatesQuery,
  useCreateTemplateMutation,
  useRenameTemplateMutation,
  useDeleteTemplateMutation,
  useUpdateTemplateContentMutation
} = templatesApi

export type {
  CreateTemplateFolderInput,
  CreateTemplateInput,
  MoveTemplateFolderInput,
  MoveTemplateFolderResponse,
  PatchTemplateInput,
  RenameTemplateInput,
  Template,
  TemplateFolder,
  UpdateTemplateContentInput
}
